/*
 * Tiny fetch wrapper for the AquaStar admin console.
 *
 * Auth model — uses the same Gateway-native auth path as Vault,
 * BolusNotes, and the iOS AquaStar app:
 *   - JWT comes back from POST /auth/login on the Gateway (NOT a
 *     bespoke admin auth service).
 *   - We persist the accessToken in sessionStorage so a closed tab
 *     logs the user out. (Tab-scoped is appropriate for an internal
 *     admin console.)
 *   - Every API call carries Authorization: Bearer <accessToken>.
 *   - 401 from any handler clears the token and bubbles to callers,
 *     which let the route guard redirect to /login.
 *
 * URL building goes through lib/config.ts so the appId 'aquastar'
 * is centralized — never hard-coded at call sites.
 */

import { lockedSignInMessage } from './accountLock';
import { APP_ID, GATEWAY_BASE, appFnUrl } from './config';

const TOKEN_KEY = 'aquastar.admin.jwt';

/** Set on a 401 so the Login page can show "your session timed out". */
export const SESSION_EXPIRED_KEY = 'aquastar.admin.sessionExpired';
/** Event the AuthProvider listens for to redirect to /login on expiry. */
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';
// Guard so a burst of concurrent 401s only signals once.
let sessionExpiredSignaled = false;

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) {
    sessionStorage.setItem(TOKEN_KEY, token);
    // New session — re-arm the expiry signal + clear any stale timeout flag.
    sessionExpiredSignaled = false;
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  } else {
    sessionStorage.removeItem(TOKEN_KEY);
    // A null access token means logged out → drop the refresh token too.
    sessionStorage.removeItem(REFRESH_KEY);
  }
}

function authHeader(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// ─── Silent refresh (P1-JWT03) ────────────────────────────────────────────
// The refresh token is the long-lived (~7d) credential that mints fresh
// short-lived access tokens. sessionStorage (tab-scoped; bearer model —
// cookie-mode deferred per PLAN_NEXUS_PHASE_2B_VI_AUTH §10).
const REFRESH_KEY = 'aquastar.admin.refresh';

export function getRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_KEY);
}
export function setRefreshToken(token: string | null): void {
  if (token) sessionStorage.setItem(REFRESH_KEY, token);
  else sessionStorage.removeItem(REFRESH_KEY);
}

// Single-flight: concurrent 401s share ONE /auth/refresh call. The refresh
// token is single-use + rotates each use (server theft-detection), so parallel
// refreshes would consume the same token and trip a family-revoke. One in-flight
// refresh; the rest await it.
let refreshInFlight: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const rt = getRefreshToken();
  if (!rt) return false;
  try {
    // window.fetch (NOT authedFetch) — must not recurse on its own 401.
    const res = await window.fetch(`${GATEWAY_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });
    if (!res.ok) { setToken(null); return false; }
    const data = (await res.json().catch(() => null)) as
      | { accessToken?: string; refreshToken?: string }
      | null;
    if (!data?.accessToken) { setToken(null); return false; }
    setToken(data.accessToken);
    if (data.refreshToken) setRefreshToken(data.refreshToken); // rotation
    return true;
  } catch {
    return false;
  }
}
function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

/**
 * fetch wrapper that transparently recovers an expired access token: on a 401
 * for an AUTHED request with a stored refresh token, single-flight
 * /auth/refresh then RETRY once with the new token. Refresh failure → original
 * 401 returned so handle() runs the logout path. Unauthed calls pass through.
 */
export async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const res = await window.fetch(input, init);
  if (res.status !== 401) return res;
  const headers = new Headers(init.headers as HeadersInit | undefined);
  if (!headers.has('Authorization') || !getRefreshToken()) return res;
  const ok = await refreshAccessToken();
  if (!ok) return res;
  const t = getToken();
  if (t) headers.set('Authorization', `Bearer ${t}`);
  return window.fetch(input, { ...init, headers });
}

async function readJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(res.status, `Non-JSON response: ${text.slice(0, 200)}`);
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    // Token rejected (expired/invalid). Clear it, flag a session timeout so the
    // Login page can explain, and signal the app to redirect to /login —
    // instead of leaving a raw error on the page. Guarded so a burst of
    // concurrent 401s only signals once.
    setToken(null);
    if (typeof window !== 'undefined' && !sessionExpiredSignaled) {
      sessionExpiredSignaled = true;
      sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    }
  }
  if (!res.ok) {
    const body = await readJson<{ error?: string; message?: string }>(res).catch(
      () => undefined,
    );
    // Prefer `message` (human-readable) over `error` (machine code).
    // Handlers like aquastarStaffGrant/Revoke/InviteCreate consistently
    // surface friendly copy in `message` and a SCREAMING_SNAKE token
    // in `error` — showing the latter to end users is needlessly
    // hostile.
    const msg =
      body?.message ?? body?.error ?? `HTTP ${res.status} ${res.statusText}`;
    throw new ApiError(res.status, msg, body);
  }
  return readJson<T>(res);
}

/**
 * Gateway login response shape — confirmed against
 * GatewayAdapter.swift in the iOS prototype (the canonical reference
 * for the platform-native auth response). Fields we don't use here
 * are typed loosely; we only depend on accessToken.
 */
export interface GatewayUser {
  userId: string;
  email: string;
  givenName?: string;
  status?: 'active' | 'disabled' | 'pending_verification' | string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken?: string;
  user: GatewayUser;
  mfaRequired?: boolean;
}

/**
 * POST /auth/login on the Gateway — the platform-native auth path
 * shared by Vault, BolusNotes, and the iOS AquaStar app.
 *
 * URL: /auth/login (NOT /v1/auth/login). The Gateway mounts native
 * auth routes at the root; the /v1/ prefix is reserved for
 * app-scoped routes (/v1/app/:appId/...). The iOS app calls the
 * exact same endpoint at Network+Auth.swift:144, and Gateway tests
 * cover it at __tests__/auth-security.test.ts.
 *
 * Payload includes `appId: APP_ID` ('aquastar') so the Gateway can
 * scope the JWT to this app — same payload as iOS at
 * Network+Auth.swift:150.
 *
 * Response shape (confirmed via GatewayAdapter.swift:25–62):
 *   { accessToken, user: { userId, email, givenName, status }, mfaRequired? }
 *
 * We persist `accessToken` as the bearer token. `mfaRequired` and
 * `user.status === 'pending_verification'` are surfaced as errors;
 * admin operators are expected to be fully provisioned before they
 * appear in the aquastar_staff table.
 */
export async function login(
  email: string,
  password: string,
): Promise<LoginResponse> {
  const res = await fetch(`${GATEWAY_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, appId: APP_ID }),
  });
  let body: LoginResponse;
  try {
    body = await handle<LoginResponse>(res);
  } catch (err) {
    // AQUASTAR-542: say until when, and that a password reset unlocks now,
    // instead of the server's "too many failed attempts; account
    // temporarily locked".
    const eb = err instanceof ApiError ? (err.body as { code?: string; lockedUntil?: string | null } | undefined) : undefined;
    if (err instanceof ApiError && eb?.code === 'ACCOUNT_LOCKED') {
      throw new ApiError(err.status, lockedSignInMessage(eb.lockedUntil), err.body);
    }
    throw err;
  }

  if (body.mfaRequired) {
    throw new ApiError(
      200,
      'Multi-factor authentication is required for this account. MFA flow is not yet implemented in the admin console — please use the iOS app to complete sign-in for now.',
      body,
    );
  }
  if (body.user?.status === 'pending_verification') {
    throw new ApiError(
      200,
      'Email verification is pending for this account. Please complete verification before signing in.',
      body,
    );
  }
  if (!body.accessToken) {
    throw new ApiError(200, 'Login response missing accessToken.', body);
  }

  setToken(body.accessToken);
  if (body.refreshToken) setRefreshToken(body.refreshToken);
  return body;
}

export function logout(): void {
  setToken(null);
}

/**
 * Gateway-tier user profile shape returned by GET /auth/me. We only
 * use a subset (email, displayName, givenName) but type the rest
 * loosely so additions don't break us.
 */
export interface AuthMe {
  userId: string;
  email: string;
  emailVerified?: boolean;
  tier?: string;
  status?: string;
  givenName?: string;
  familyName?: string;
  displayName?: string;
  createdAt?: string;
  groups?: string[];
  mfaEnabled?: boolean;
  /** users.is_platform_admin — gates the /platform nav link + access
   *  to the Nexus admin-ui pages. Stale-until-refresh by design (carried
   *  on the JWT subject at mint time). */
  isPlatformAdmin?: boolean;
}

/**
 * GET /auth/me — Gateway-tier user profile for the current JWT.
 *
 * Used on boot/refresh to repopulate user info that isn't carried in
 * the JWT itself, AND to validate that the token is still good
 * before we ask aquastar_staff. Same endpoint Vault and the iOS app
 * use (Gateway-native, not app-scoped).
 */
export async function getMe(): Promise<AuthMe> {
  const res = await authedFetch(`${GATEWAY_BASE}/auth/me`, {
    method: 'GET',
    headers: { ...authHeader() },
  });
  return handle<AuthMe>(res);
}

/**
 * The app_functions dispatcher wraps every handler return in this
 * envelope (carried over from the iOS shim cutover):
 *
 *   { success: true, message: ['Success'], data: <handler's return> }
 *
 * On handler errors, `success: false` and `data: null`. We unwrap
 * automatically inside getFn/postFn so consumers always work with the
 * inner payload directly. `/auth/login`, `/auth/me`, etc. are
 * Gateway-native (not dispatched through app_functions) and don't
 * carry this envelope — they go through `login()` / `getMe()` which
 * read the response shape directly.
 */
interface AppFnEnvelope<T> {
  success: boolean;
  message?: string[] | string | null;
  data: T | null;
}

function unwrapAppFn<T>(envelope: AppFnEnvelope<T> | T): T {
  // Best-effort detection: if it has the shape of the wrapper, unwrap;
  // otherwise pass through. Some handlers may have called
  // ctx.response.raw() with a different shape — those bypass the
  // wrapper and we don't want to corrupt them.
  if (
    envelope !== null &&
    typeof envelope === 'object' &&
    'success' in envelope &&
    'data' in envelope
  ) {
    const env = envelope as AppFnEnvelope<T>;
    if (env.success === false) {
      const msg = Array.isArray(env.message)
        ? env.message.join(' ')
        : env.message ?? 'Handler returned success=false';
      throw new ApiError(200, msg, env);
    }
    return env.data as T;
  }
  return envelope as T;
}

/**
 * Call an app-scoped function handler.
 *
 *   getFn<UsersListResponse>('aquastarListUsers', { cursor, limit })
 *
 * Always GET — the platform's idempotent read shape. Uses URL +
 * window.location.origin as base so a relative `appFnUrl()` (the
 * production case — see config.ts on why GATEWAY_BASE is empty)
 * resolves correctly. An absolute base passes through URL unchanged.
 *
 * Auto-unwraps the dispatcher envelope; consumers see the handler's
 * raw return shape.
 */
export async function getFn<T>(
  name: string,
  params?: Record<string, string | number | undefined>,
): Promise<T> {
  const url = new URL(appFnUrl(name), window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  const res = await authedFetch(url.toString(), {
    method: 'GET',
    headers: { ...authHeader() },
  });
  return unwrapAppFn<T>(await handle<AppFnEnvelope<T> | T>(res));
}

/**
 * Call a write-shaped app function. Sends JSON body; same auth + error
 * handling + envelope unwrap as getFn.
 */
export async function postFn<T>(
  name: string,
  body?: unknown,
): Promise<T> {
  const res = await authedFetch(appFnUrl(name), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return unwrapAppFn<T>(await handle<AppFnEnvelope<T> | T>(res));
}

/**
 * PUT to an app_function endpoint — admin mutations such as
 * aquastarUpdateDeviceModel. Same envelope handling as postFn.
 */
export async function putFn<T>(
  name: string,
  body?: unknown,
): Promise<T> {
  const res = await authedFetch(appFnUrl(name), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return unwrapAppFn<T>(await handle<AppFnEnvelope<T> | T>(res));
}

/**
 * DELETE to an app_function endpoint — admin mutations such as
 * aquastarDeleteDeviceModel. The id is sent in the JSON body.
 */
export async function delFn<T>(
  name: string,
  body?: unknown,
): Promise<T> {
  const res = await authedFetch(appFnUrl(name), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return unwrapAppFn<T>(await handle<AppFnEnvelope<T> | T>(res));
}

/**
 * Call an app-scoped REST endpoint that is NOT an app_function — e.g. the
 * Issue Tracker routes mounted at /v1/app/:appId/issues/*. These return raw
 * JSON (no AppFnEnvelope), so we share the same auth + 401 + error handling
 * as getFn/postFn but skip the envelope unwrap.
 *
 *   appRest<{ items: Issue[] }>('GET', '/issues?status=new')
 *   appRest<Issue>('POST', '/issues', { typeKey, title })
 *
 * `path` is appended to `/v1/app/<appId>` and must start with '/'.
 */
export async function appRest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const url = `${GATEWAY_BASE}/v1/app/${APP_ID}${path}`;
  const res = await authedFetch(url, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handle<T>(res);
}

/**
 * Call a PLATFORM-admin REST endpoint mounted at /v1/admin/* (e.g. the
 * app_jobs management API). Same auth/401/error handling as appRest, raw JSON
 * (no AppFnEnvelope). These endpoints are gated server-side by
 * `isPlatformAdmin` — a non-platform-admin caller gets 403, so callers should
 * only surface admin-REST-backed UI when useAuth().staff.isPlatformAdmin.
 *
 *   adminRest<{ jobs: AppJob[] }>('GET', '/apps/aquastar/jobs')
 *   adminRest<{ job: AppJob }>('PATCH', '/apps/aquastar/jobs/sweepBrokerConnectivity', { enabled: false })
 *
 * `path` is appended to `/v1/admin` and must start with '/'.
 */
export async function adminRest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const url = `${GATEWAY_BASE}/v1/admin${path}`;
  const res = await authedFetch(url, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handle<T>(res);
}

/**
 * Call a signed-in-user REST endpoint mounted at /v1/auth/* (e.g. personal
 * access tokens). Same auth/401/error handling as adminRest, raw JSON.
 *
 *   authRest<{ tokens: AccessToken[] }>('GET', '/access-tokens')
 *
 * `path` is appended to `/v1/auth` and must start with '/'.
 */
export async function authRest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const url = `${GATEWAY_BASE}/v1/auth${path}`;
  const res = await authedFetch(url, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handle<T>(res);
}
